/**
 * Game physics and world constants for Warmancer space combat
 */
// Simulation rates
export const TICK_RATE = 60;
export const TICK_DELTA = 1 / TICK_RATE;
export const NETWORK_RATE = 20;
export const TICKS_PER_NETWORK = TICK_RATE / NETWORK_RATE; // 3
// World bounds (square: -HALF_SIZE to +HALF_SIZE on both axes)
export const WORLD_HALF_SIZE = 400;
// Fighter ship stats
export const FIGHTER_MASS = 10;
export const FIGHTER_THRUST = 100;
export const FIGHTER_STRAFE_THRUST = 100; // lateral thrusters equal to main engines
export const FIGHTER_ROTATION_SPEED = 12.0; // rad/s — snaps to mouse aim nearly instantly
export const FIGHTER_RADIUS = 1.0;
export const FIGHTER_MAX_HEALTH = 100;
// Afterburner
export const AFTERBURNER_MULT = 3.0;
export const BATTERY_MAX = 100;
export const BATTERY_DRAIN_PER_SEC = 20;
export const BATTERY_REGEN_PER_SEC = 10;
// Gravity — inverse-linear model: F = G * m1 * m2 / r (not r²).
// 1/r is physically correct for 2D (flux spreads over circumference ~ r).
// Force halves when distance doubles, giving strong flyby slingshots.
// Planet surface ~164 N (needs afterburner), flyby at r=90 ~111 N (exceeds thrust).
export const GRAVITY_CONSTANT = 0.005;
// Gravity range cutoff: gravity extends to radius * this, then drops to zero.
// Creates gravity-free zones in the outer map where ships fly unaffected.
export const GRAVITY_RANGE_MULTIPLIER = 3;
// Planet (center of map)
export const PLANET_X = 0;
export const PLANET_Y = 0;
export const PLANET_RADIUS = 60;
export const PLANET_MASS = 200000; // 4x for doubled radius (maintains surface gravity)
// Moon (orbits planet)
export const MOON_RADIUS = 16;
export const MOON_MASS = 12000; // Surface force ~35N at surface, deflects at flyby distances
export const MOON_ORBIT_RADIUS = 200; // Just beyond planet gravity range (180). Moon gravity (48) overlaps planet field from 152-180.
export const MOON_ORBIT_SPEED = 0.05; // rad/s
// Asteroids
export const ASTEROID_COUNT_MIN = 25;
export const ASTEROID_COUNT_MAX = 30;
export const ASTEROID_RADIUS_MIN = 2;
export const ASTEROID_RADIUS_MAX = 8;
export const ASTEROID_MASS_PER_RADIUS = 100; // mass = radius * this
// Velocity damping — soft speed limit (Star Control 2 style)
// Per-tick multiplier: 0.995 means ship retains 99.5% of speed each tick.
// Rapier formula: v_new = v_old / (1 + dt * damping_coeff)
// Terminal velocity = FIGHTER_THRUST / (FIGHTER_MASS * SHIP_LINEAR_DAMPING)
export const SHIP_VELOCITY_DAMPING = 0.995;
export const SHIP_LINEAR_DAMPING = (1 / SHIP_VELOCITY_DAMPING - 1) / TICK_DELTA;
// Collision
export const BOUNCE_RESTITUTION = 0.7;
// Spawn safety
export const SPAWN_MIN_DISTANCE_FROM_PLANET = PLANET_RADIUS + 20;
export const SPAWN_MAX_DISTANCE_FROM_CENTER = WORLD_HALF_SIZE - 20;
// Asteroid sync rate: 20Hz (every 3 ticks, same as snapshot rate).
// Matches snapshot rate to minimize N-body divergence between client and server.
// At 20Hz, asteroid positions drift at most ~50ms of chaotic N-body gravity,
// keeping divergence small enough for reliable client-side collision prediction.
// Bandwidth cost: ~5.6 KB/s per player (acceptable for real-time multiplayer).
export const TICKS_PER_ASTEROID_SYNC = 3;
// Disconnect grace period
export const DISCONNECT_GRACE_MS = 3000;
// Protocol message types
export const MSG_INPUT = 0x01;
export const MSG_SNAPSHOT = 0x02;
export const MSG_PLAYER_JOIN = 0x03;
export const MSG_PLAYER_LEAVE = 0x04;
export const MSG_WORLD_SETUP = 0x05;
export const MSG_ASTEROID_SYNC = 0x06;
export const MSG_SCENERY_SETUP = 0x07;
export const MSG_PARTICLE_DEFAULTS_UPDATED = 0x08;
export const MSG_REQUEST_PARTICLE_BROADCAST = 0x09;
// Scenery setup protocol sizes (bytes)
export const SCENERY_SETUP_HEADER = 6;
export const SCENERY_ZONE_ENTRY_SIZE = 25;
// Input bitfield flags
export const INPUT_THRUST = 1 << 0; // bit 0: W/Up
export const INPUT_STRAFE_LEFT = 1 << 1; // bit 1: A/Left (lateral thrust)
export const INPUT_STRAFE_RIGHT = 1 << 2; // bit 2: D/Right (lateral thrust)
export const INPUT_BRAKE = 1 << 3; // bit 3: S/Down
export const INPUT_AFTERBURNER = 1 << 4; // bit 4: Ctrl/Shift
export const INPUT_FIRE = 1 << 5; // bit 5: Left mouse button
// Entity flags in snapshot
export const FLAG_ALIVE = 1 << 0;
export const FLAG_AFTERBURNER = 1 << 1;
export const FLAG_THRUSTING = 1 << 2;
export const FLAG_BRAKING = 1 << 3;
export const FLAG_STRAFING_LEFT = 1 << 4;
export const FLAG_STRAFING_RIGHT = 1 << 5;
export const FLAG_FIRING = 1 << 6;
// Snapshot sizes (bytes)
export const INPUT_MSG_SIZE = 14; // 10 bytes + 4 bytes f32 targetAngle
export const SNAPSHOT_HEADER_SIZE = 14;
export const ENTITY_SIZE = 30;
// Auto-cannon weapon
export const CANNON_FIRE_RATE = 30; // rounds per second
export const CANNON_FIRE_INTERVAL_TICKS = 2; // 60Hz / 30rps = every 2 ticks
export const CANNON_SPEED = 200; // units/sec tracer velocity
export const CANNON_LIFETIME = 0.8; // seconds before tracer despawns
export const CANNON_SPREAD_RAD = 0.1; // +/-0.1 rad (~5.7 degrees) — visible spray cone
export const CANNON_MUZZLE_OFFSET = 1.5; // units forward from ship center
export const MAX_TRACERS = 200; // pool size (covers 3+ ships firing at 30rps)
// Entity ID for celestial bodies (reserved range)
export const ENTITY_ID_PLANET = 0xFFFFFF01;
export const ENTITY_ID_MOON = 0xFFFFFF02;
//# sourceMappingURL=constants.js.map